import {TrackingManagerV1} from '@volkswagen-onehub/tracking-service';
import {GfaLocaleServiceV1} from '@volkswagen-onehub/gfa-locale-service';
import {
  TrackingDownloadPdfClickEventData,
  TrackingLinkClickEventData,
} from '@volkswagen-onehub/whitelabel-e-mobility-commons';

export const trackingModelIdSplitter = (id: string): [string, string] => {
  const [modelId, ...rest] = id.split('$');
  const modelTrimFeatures = rest.join('$');

  return [modelId.trim(), modelTrimFeatures.trim()];
};

export enum VwTrackingEvents {
  VWBasic_Checkbox_Click = 'VWBasic_Checkbox_Click',
  VWBasic_FilterSelect_Click = 'VWBasic_FilterSelect_Click',
  VWBasic_FilterReset_Click = 'VWBasic_FilterReset_Click',
  VWBasic_VehicleLink_Click = 'VWBasic_VehicleLink_Click',
  VWBasic_VehicleSelect_Click = 'VWBasic_VehicleSelect_Click',
  VWBasic_EngineSelect_Click = 'VWBasic_EngineSelect_Click',
  VWBasic_WheelsChange_Click = 'VWBasic_WheelsChange_Click',
  VWBasic_eMobilityTools_Load = 'VWBasic_eMobilityTools_Load',
  VWBasic_Link_Click = 'VWBasic_Link_Click',
  VWBasic_Download_Click = 'VWBasic_Download_Click',
  VWBasic_VehicleButton_Click = 'VWBasic_VehicleButton_Click',
  VWBasic_ContentTab_Click = 'VWBasic_ContentTab_Click',
  VWBasic_Button_Click = 'VWBasic_Button_Click',
}
type TrackingData = Record<
  string,
  Record<string, unknown> | Record<string, any>
>;

export interface CheckBoxClickEventData extends TrackingData {
  eventInfo: {
    contentId: string;
  };
  link: {
    name: string;
  };
}
export interface FilterSelectTrackingData extends TrackingData {
  filter:
    | {
        type: 'multi_value';
        values: (number | string)[];
        name: string;
      }
    | {
        type: 'single_value';
        values: [number | string];
        name: string;
      }
    | {
        type: 'range';
        upperbound: number;
        lowerbound: number;
        name: string;
      };
}

export interface FilterResetTrackingData extends TrackingData {
  filter: {
    type: 'multi_value';
    values: number[];
    name: string;
  };
}

export interface VehicleSelectData extends TrackingData {
  configuration: {
    EquipmentlineName: string;
    CarlineId: string;
    CarlineName: string;
    SalesgroupId: string;
    SalesgroupName: string;
    ModelId: string;
    ModelTrimFeatures: string;
    ModelName: string;
    EnginePowerHPKW: string | undefined;
    // Range only
    WheelsBasicType?: string;
    WheelsBasicSize?: number;
  };
}

export interface VehicleEngineSelectData extends TrackingData {
  configuration: {
    ModelId: string;
    ModelTrimFeatures: string;
    ModelName: string;
    EnginePowerHPKW: string | undefined;
    // Range only
    WheelsBasicType?: string;
    WheelsBasicSize?: number;
  };
}

export interface VehicleLinkClickData extends TrackingData {
  eventInfo: {
    contentId: string;
  };
  link: TrackingLinkClickEventData;
}

export interface BasicLinkClickData extends TrackingData {
  eventInfo: {
    contentId: string;
  };
  link: TrackingLinkClickEventData;
}

export interface BasicDownloadPdfClickData extends TrackingData {
  link: TrackingLinkClickEventData;
  file: TrackingDownloadPdfClickEventData;
}

export interface BasicButtonClick extends TrackingData {
  eventInfo: {
    contentId: string;
  };
  link: TrackingLinkClickEventData;
}

export interface BasicContentTabClick extends TrackingData {
  eventInfo: {
    contentId: string;
  };
}

export interface WheelsChangeData extends TrackingData {
  configuration: {
    WheelsBasicType?: 'summer' | 'winter' | 'allSeason';
    WheelsBasicSize: number | undefined;
  };
}

export interface VwPayloadsMap {
  [VwTrackingEvents.VWBasic_Checkbox_Click]: CheckBoxClickEventData;
  [VwTrackingEvents.VWBasic_FilterSelect_Click]: FilterSelectTrackingData;
  [VwTrackingEvents.VWBasic_FilterReset_Click]: FilterResetTrackingData;
  [VwTrackingEvents.VWBasic_eMobilityTools_Load]: undefined;
  [VwTrackingEvents.VWBasic_EngineSelect_Click]: VehicleEngineSelectData;
  [VwTrackingEvents.VWBasic_VehicleSelect_Click]: VehicleSelectData;
  [VwTrackingEvents.VWBasic_VehicleLink_Click]: VehicleLinkClickData;
  [VwTrackingEvents.VWBasic_WheelsChange_Click]: WheelsChangeData;
  [VwTrackingEvents.VWBasic_Link_Click]: BasicLinkClickData;
  [VwTrackingEvents.VWBasic_Download_Click]: BasicDownloadPdfClickData;
  [VwTrackingEvents.VWBasic_VehicleButton_Click]: BasicButtonClick;
  [VwTrackingEvents.VWBasic_Button_Click]: BasicButtonClick;
  [VwTrackingEvents.VWBasic_ContentTab_Click]: BasicContentTabClick;
}

export interface ConfigurationTrackingData {
  readonly CarlineId?: string;
  readonly CarlineName?: string;
  readonly SalesgroupId?: string;
  readonly SalesgroupName?: string;
  readonly EquipmentlineName?: string;
  readonly ModelId?: string;
  readonly ModelTrimFeatures?: string;
  readonly ModelName?: string;
  // might be unknown
  readonly EnginePowerHPKW?: string | undefined;
  // range only
  readonly WheelsBasicType?: string;
  readonly WheelsBasicSize?: number;
}

export interface CommonTrackingData {
  readonly eventInfo: {
    readonly brand: 'commercial' | 'passenger';
  };
  readonly configuration: ConfigurationTrackingData;
}

export type VwTrack = <T extends VwTrackingEvents>(
  action: T,
  commonTrackingData: CommonTrackingData,
  data: VwPayloadsMap[T]
) => void;

export function getVwTrack(
  trackingManager: TrackingManagerV1,
  localeService: GfaLocaleServiceV1
): VwTrack {
  const {language, countryCode} = localeService;

  return function vwTrack<T extends VwTrackingEvents>(
    action: T,
    commonTrackingData: CommonTrackingData,
    data: VwPayloadsMap[T]
  ): void {
    trackingManager.track(action, {
      ...data,
      eventInfo: {
        ...commonTrackingData.eventInfo,
        ...data?.eventInfo,
      },
      environment: {
        ...data?.environment,
        language,
        country: countryCode,
        // environment.featureAppId is provided by CMS
        // This is not our normal feature app id, but the special tracking id
        // that can be set up in the AEM feature app configuration.
      },
      configuration: {
        ...commonTrackingData.configuration,
        ...data?.configuration,
      },
    });
  };
}

export const getCommonTrackingData = (
  commercial: boolean,
  configurationData: ConfigurationTrackingData
): CommonTrackingData => {
  return {
    eventInfo: {
      brand: commercial ? 'commercial' : 'passenger',
    },
    configuration: configurationData,
  };
};
